// The public worksheet library: one unit per stage and one topic per step, with the copy and URLs in library.json.
// The site builds its grade, unit and topic pages from this; the app links to them.
import libraryData from './library.json';
import { LADDER, stageInfo } from './decisions';

export interface Topic {
  stage: number; step: number;
  slug: string;           // URL segment, unique within its unit
  name: string;           // the search phrase the page is built around ("Long division with two-digit divisors")
  about: string;          // one sentence on what the worksheet practices
  title: string;          // the step's own title, as printed on the sheet
  hint: string;           // the method in a sentence, as printed in the New skill box
  path: string;           // /worksheets/<unit>/<topic>/
}

export interface Unit {
  stage: number; slug: string; name: string; about: string;
  title: string;          // the stage's own title
  grades: string;         // "Grades 4 to 5"
  gradeSlugs: string[];   // the grade hubs that list this unit
  path: string;           // /worksheets/<unit>/
  topics: Topic[];
}

export interface Grade { slug: string; label: string; short: string; chip: string; path: string; units: Unit[] }

const GRADE_ORDER = ['pre-k', 'kindergarten', ...Array.from({ length: 12 }, (_, i) => `grade-${i + 1}`)];
const gradeLabel = (slug: string) => slug === 'pre-k' ? 'Pre-K' : slug === 'kindergarten' ? 'Kindergarten' : `Grade ${slug.slice(6)}`;
const gradeShort = (slug: string) => slug === 'pre-k' ? 'Pre-K' : slug === 'kindergarten' ? 'K' : slug.slice(6);
/** The grade pickers: "Pre-K", "Kindergarten", "1st" … "12th". */
const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
const gradeChip = (slug: string) => slug === 'pre-k' ? 'Pre-K' : slug === 'kindergarten' ? 'Kindergarten' : ordinal(Number(slug.slice(6)));

/** "Grades 4 to 5" → ['grade-4', 'grade-5']; "PreK to K" → ['pre-k', 'kindergarten']. */
export function gradeSlugs(grades: string): string[] {
  const one = (g: string) => g === 'PreK' ? 'pre-k' : g === 'K' ? 'kindergarten' : `grade-${g}`;
  const m = /^(?:Grades? )?(PreK|K|\d+)(?: to (K|\d+))?$/.exec(grades.trim());
  if (!m) return [];
  const a = GRADE_ORDER.indexOf(one(m[1])), b = m[2] ? GRADE_ORDER.indexOf(one(m[2])) : a;
  return GRADE_ORDER.slice(a, b + 1);
}

/** Short grade label for cards: "Grades 4–5", "Pre-K–K", "Grade 3". */
export function gradeRangeLabel(grades: string): string {
  const s = gradeSlugs(grades);
  if (!s.length) return grades;
  if (s.length === 1) return gradeLabel(s[0]);
  const a = gradeShort(s[0]), b = gradeShort(s[s.length - 1]);
  return /^\d/.test(a) ? `Grades ${a}–${b}` : `${a}–${b}`;
}

export const UNITS: Unit[] = (libraryData as any).units.map((u: any) => {
  const st = stageInfo(u.stage);
  const path = `/worksheets/${u.slug}/`;
  return {
    stage: u.stage, slug: u.slug, name: u.name, about: u.about, title: st.title, grades: st.grades,
    gradeSlugs: gradeSlugs(st.grades), path,
    topics: u.topics.map(([slug, name, about]: [string, string, string], i: number) => ({
      stage: u.stage, step: i + 1, slug, name, about, title: st.steps[i].title, hint: st.steps[i].hint, path: `${path}${slug}/`,
    })),
  };
});

if (UNITS.length !== LADDER.length) throw new Error('library.json must have one unit per stage');

export const GRADES: Grade[] = GRADE_ORDER.map((slug) => ({
  slug, label: gradeLabel(slug), short: gradeShort(slug), chip: gradeChip(slug), path: `/worksheets/${slug}/`,
  units: UNITS.filter((u) => u.gradeSlugs.includes(slug)),
}));

export const unitOf = (stage: number) => UNITS[stage - 1];
export const topicOf = (stage: number, step: number) => UNITS[stage - 1]?.topics[step - 1];
export const ALL_TOPICS: Topic[] = UNITS.flatMap((u) => u.topics);

/** The topic before and after in ladder order (crossing units). */
export function neighbors(t: Topic): { prev: Topic | null; next: Topic | null } {
  const i = ALL_TOPICS.findIndex((x) => x.stage === t.stage && x.step === t.step);
  return { prev: ALL_TOPICS[i - 1] ?? null, next: ALL_TOPICS[i + 1] ?? null };
}
